import { get } from 'lodash';

import { RoleId } from '@wix/members-domain-ts';
import { assignRole, unassignRole } from '@wix/ambassador-members-v1-role/http';
import { HttpClient } from '@wix/http-client';

import {
  fetchMember,
  setMember,
  removeMember,
  openChat,
  followOrUnfollow,
  blockMember,
  reportMember,
} from '../members/actions';
import { getAppComponent, getSignedInstance } from '../site/selectors';
import { openModal, openModalWithCallback, openNotification } from '../../actions/open-modal';
import { requestLogin } from '../../services/request-login';
import { enterProfileEditing } from '../profile-page/actions';
import { openJoinCommunityModal, openLeaveCommunityModal } from '../community/actions';
import { openContactPage } from '../../services/navigation';
import { findMemberByUid, findMemberNameByUid, getCurrentMember } from '../members/selectors';
import { openManageBadgesModal } from '../badges/actions';
import Wix from '../../services/wix-sdk-polyfill';
import { removeMemberFromList } from '../members-page/actions';
import { BIService } from '../../services/bi';
import { MEMBERS_LIST_ORIGIN } from '../../constants/bi';
import * as MODAL_TYPE from '../../constants/modal-types';

export const ROLES_ACTIONS_CLICK = 'roles/ACTION_CLICK';
export const SET_ROLES_ACTIONS = 'roles/SET_ROLES_ACTIONS';

const roleMap = {
  forum_moderator: 'FORUM_MODERATOR',
  blog_writer: 'BLOG_WRITER',
  blog_editor: 'BLOG_EDITOR',
  blocked: 'BLOCKED',
};

const toRoleKey = (role) => get(roleMap, role, 'UNKNOWN');

const ReportCategory = {
  Spam: 'SPAM',
  Impersonation: 'IMPERSONATION',
  Harassment: 'HARASSMENT',
  Other: 'OTHER',
};

const reportCategoryMap = [
  ReportCategory.Spam,
  ReportCategory.Impersonation,
  ReportCategory.Harassment,
  ReportCategory.Other,
];

export const rolesActionClicked = (memberId, roleId, formOrigin) => ({
  type: ROLES_ACTIONS_CLICK,
  roleId,
  memberId,
  formOrigin,
});

export const oppositeRolesActions = {
  [RoleId.BLOCK_MEMBER]: RoleId.UNBLOCK_MEMBER,
  [RoleId.SET_FORUM_MODERATOR]: RoleId.UNSET_FORUM_MODERATOR,
  [RoleId.SET_BLOG_WRITER]: RoleId.UNSET_BLOG_WRITER,
};

export const rolesActionsWithPictures = [
  RoleId.SET_FORUM_MODERATOR,
  RoleId.UNSET_FORUM_MODERATOR,
  RoleId.SET_BLOG_WRITER,
  RoleId.UNSET_BLOG_WRITER,
];

export const applyRoleThroughAPI = ({ roles, uid }, roleId) => (dispatch, getState) => {
  const state = getState();
  const method = roles.indexOf(roleId) === -1 ? assignRole : unassignRole;
  const httpClient = new HttpClient({ getAppToken: () => getSignedInstance(state) });

  return httpClient
    .request(method({ roleKey: toRoleKey(roleId), memberId: uid }))
    .then(() => dispatch(fetchMember(uid)))
    .then((member) => dispatch(setMember(member)));
};

export const applyRole = (member, role) => (dispatch) => {
  return dispatch(applyRoleThroughAPI(member, role.roleId));
};

export const openDeleteMemberModal = (memberId, done) => (dispatch) => {
  const onConfirm = () => dispatch(removeMember(memberId)).then(() => dispatch(done(memberId)));
  dispatch(openModalWithCallback(RoleId.DELETE_MEMBER, onConfirm));
};

export const contactPage = (uid) => () => {
  const metasiteId = Wix.Utils.getInstanceValue('metaSiteId');
  const styleParams = Wix.Styles.getStyleParams();
  const isEditorX = styleParams && styleParams.booleans && styleParams.booleans.responsive;
  openContactPage(metasiteId, uid, isEditorX);
};

export const joinCommunity = {
  roleId: RoleId.JOIN_COMMUNITY,
  action: 'MemberRoles.action_set.community',
};

export const unfollowRoleAction = {
  roleId: RoleId.FOLLOW_UNFOLLOW,
  action: 'members-page.unfollow',
};

export const openChatRoleAction = {
  roleId: RoleId.OPEN_CHAT,
  action: 'members-page.message',
};

// TODO: this should not be an action, it's used by selectors!
export const withIcons = (rolesActions = [], rolesMap = {}) =>
  rolesActions.map((roleAction) => ({
    ...roleAction,
    ...(rolesMap[roleAction.roleId] || {}),
  }));

export const customRolesActionsMap = {
  [RoleId.JOIN_COMMUNITY]: () => openJoinCommunityModal(),
  [RoleId.LEAVE_COMMUNITY]: () => openLeaveCommunityModal(),
  [RoleId.EDIT]: () => enterProfileEditing(),
  [RoleId.ANONYMOUS_REPORT]: () => () => requestLogin(),
  [RoleId.DELETE_MEMBER]: ({ memberId, onDeleteMember }) => openDeleteMemberModal(memberId, onDeleteMember),
  [RoleId.CONTACT_PAGE]: ({ memberId }) => contactPage(memberId),
  [RoleId.MANAGE_BADGES]: ({ memberId }) => openManageBadgesModal(memberId),
  [RoleId.OPEN_CHAT]: ({ memberId }) => openChat(memberId),
  [RoleId.FOLLOW_UNFOLLOW]: ({ memberId }) => followOrUnfollow(memberId),
  [RoleId.MEMBER_BLOCK_MEMBER]: ({ memberId }) => openBlockMemberModal(memberId),
  [RoleId.REPORT_MEMBER]: ({ memberId }) => openReportMemberModal(memberId),
};

const successTranslationMap = {
  [RoleId.BLOCK_MEMBER]: (memberName) => ({ memberName, translationKey: 'roles.block.success-message.block' }),
  [RoleId.UNBLOCK_MEMBER]: (memberName) => ({ memberName, translationKey: 'roles.block.success-message.unblock' }),
};

export const openRoleModal = (memberId, role) => (dispatch, getState) => {
  const state = getState();
  const originAppComponent = getAppComponent(state);
  const member = findMemberByUid(state, memberId);
  const roleId = role.roleId;
  const actionRole = (member.roles.includes(roleId) && oppositeRolesActions[roleId]) || roleId; // TODO: backend logic
  const hasPicture = rolesActionsWithPictures.includes(roleId);
  const props = hasPicture
    ? {
        memberName: encodeURIComponent(member.name),
        memberPicture: member.picture,
      }
    : {};

  const onConfirm = () => {
    dispatch(applyRole(member, role)).then(() => {
      if (successTranslationMap[actionRole]) {
        dispatch(openNotification(successTranslationMap[actionRole](member.name)));
      }
    });
  };
  dispatch(
    openModalWithCallback(actionRole, onConfirm, {
      originAppComponent,
      ...props,
    }),
  );
};

export const openBlockMemberModal = (memberId) => (dispatch, getState) => {
  const state = getState();
  const memberName = findMemberNameByUid(state, memberId);
  const bi = new BIService();
  const onConfirm = () => {
    dispatch(blockMember(memberId))
      .then(() => dispatch(removeMemberFromList(memberId)))
      .then(() =>
        dispatch(openNotification({ translationKey: 'roles.memberblock.success-message.block', memberName })),
      );
    bi.maActionButtonClick('block_confirm', {
      formOrigin: MEMBERS_LIST_ORIGIN,
      member_chosen: memberId,
    });
  };
  dispatch(
    openModalWithCallback(RoleId.MEMBER_BLOCK_MEMBER, onConfirm, {
      memberName,
    }),
  );
};

export const openReportMemberModal = (reportedMemberId) => (dispatch, getState) => {
  const state = getState();
  const bi = new BIService();

  const onConfirm = (data) => {
    const category = reportCategoryMap[data.category];
    const description = data.description;
    const reportingMemberId = getCurrentMember(state).uid;

    dispatch(reportMember(reportedMemberId, reportingMemberId, { category, description }));

    bi.maReportMemberSubmitted(category, description, reportedMemberId);

    dispatch(openModal(MODAL_TYPE.THANKS_FOR_REPORT, {}));
  };

  dispatch(openModalWithCallback(RoleId.REPORT_MEMBER, onConfirm));
};
